import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { streamPost } from "../lib/api";

const CHIPS = [
  "Which service do I need?",
  "Is SuburbIQ right for me?",
  "What are your fees?",
];

const getSessionId = () => {
  let sid = localStorage.getItem("concierge_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("concierge_sid", sid);
  }
  return sid;
};

export const Concierge = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(getSessionId());
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    streamPost("/ai/chat", { session_id: sid.current, message: msg }, {
      onDelta: (t) =>
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + t };
          return next;
        }),
      onError: (err) =>
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: err };
          return next;
        }),
      onDone: () => setBusy(false),
    });
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="concierge-toggle"
        aria-label="Ask 80 Metrics"
        aria-expanded={open}
        className="concierge-launcher fixed z-[60] inline-flex items-center gap-2 bg-charcoal border border-gold/50 px-4 py-2.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-ivory/85 transition-[color,border-color] duration-300 hover:text-goldlight hover:border-gold shadow-md"
      >
        {open ? "Close" : "Ask 80 Metrics"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[4.5rem] lg:bottom-24 right-3 lg:right-8 z-[60] w-[calc(100vw-1.5rem)] sm:w-[400px] h-[68vh] max-h-[560px] bg-ivory border border-charcoal/25 shadow-2xl flex flex-col"
            data-testid="concierge-panel"
          >
            <div className="bg-charcoal px-5 py-4 shrink-0">
              <p className="font-serif text-xl font-medium text-ivory">Ask 80 Metrics</p>
              <p className="mt-1 text-[11px] leading-snug text-ivory/50">
                Evidence-based guidance to the right starting point. General information, not personal advice.
              </p>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="concierge-messages">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="font-serif text-lg text-charcoal/80 leading-snug">
                    Tell us what you’re trying to decide — we’ll point you to the right starting point.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {CHIPS.map((c, i) => (
                      <button
                        key={c}
                        onClick={() => send(c)}
                        data-testid={`concierge-chip-${i}`}
                        className="border border-charcoal/25 px-3.5 py-2 text-[11px] font-semibold tracking-[0.14em] uppercase text-charcoal/70 transition-[color,border-color] duration-300 hover:border-gold hover:text-golddark"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`concierge-message-${i}`}
                  className={
                    m.role === "user"
                      ? "ml-8 bg-charcoal text-ivory px-4 py-3 text-sm leading-relaxed"
                      : "mr-8 bg-white border-l-2 border-gold px-4 py-3 text-sm leading-relaxed text-charcoal/85"
                  }
                >
                  {m.content
                    ? m.content.replace(/\*\*/g, "")
                    : busy && i === messages.length - 1
                      ? <span className="inline-block w-2 h-4 bg-gold animate-pulse" />
                      : m.content}
                </div>
              ))}
            </div>

            <div className="shrink-0 border-t border-charcoal/15 px-4 py-3 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your question…"
                data-testid="concierge-input"
                className="flex-1 bg-white border border-charcoal/25 px-3.5 py-2.5 text-sm text-charcoal placeholder:text-charcoal/40 outline-none transition-colors duration-300 focus:border-gold"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="concierge-send"
                className="px-4 py-2.5 bg-charcoal text-ivory text-[11px] font-semibold tracking-[0.18em] uppercase transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink disabled:opacity-50"
              >
                Send
              </button>
            </div>
            <div className="shrink-0 border-t border-charcoal/10 px-4 py-2.5 flex justify-between text-[10px] font-semibold tracking-[0.16em] uppercase">
              <Link to="/suburbiq" onClick={() => setOpen(false)} className="text-charcoal/60 hover:text-golddark transition-colors duration-300" data-testid="concierge-suburbiq-link">
                Get my SuburbIQ report →
              </Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="text-charcoal/60 hover:text-golddark transition-colors duration-300" data-testid="concierge-contact-link">
                Discuss my next move →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
